import React from 'react';
import { Query } from 'react-apollo';
import CrossSellQuery from './CrossSellProductsQuery.gql';
import get from 'lodash.get';
import styled from 'react-emotion';
import t from '@jetshop/intl';
import Image from '@jetshop/ui/Image';
import { Price } from '../Price';
import AddFavoriteButton from '../FavoriteList/AddFavoriteButton';

const Button = styled('a')`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.pink};
  height: ${({ theme }) => theme.sizes.prodElementHeight};
  color: ${({ theme }) => theme.colors.black};
  min-width: 288px;
`;

const SectionHeading = styled('h3')`
  ${({ theme }) => theme.typography.h3};
  margin-bottom: 3rem;
  text-align: center;
`;

const BreakFlex = styled('div')`
  flex: 1 1 100%;
  width: 100%;
`;

const SectionRow = styled('div')`
  ${({ theme }) => theme.elements.fwContainer};
  padding: 2.75rem 0;

  ${({ theme }) => theme.below.md} {
    padding-bottom: ${({ theme }) => theme.space[2]};
  }
`;

const SectionRowInner = styled('div')`
  ${({ theme }) => theme.elements.container};
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const ProductSection = styled('section')`
  display: flex;
  justify-content: space-between;
  padding-right: ${({ theme }) => theme.space[2]};
  padding-left: ${({ theme }) => theme.space[2]};
  width: 100%;
  max-width: 960px;
  margin: 0 auto;

  .product-image {
    width: calc(40% - ${({ theme }) => theme.space[3]});
  }

  .product-description {
    width: 60%;

    &__action {
      margin-top: ${({ theme }) => theme.space[4]};
    }
  }

  .product-name {
    padding-bottom: ${({ theme }) => theme.space[2]};
    h4 {
      font-family: ${({ theme }) => theme.fontHeading};
      font-size: 1.5rem;
    }

    &__sub-title {
      display: block;
      color: ${({ theme }) => theme.colors.greyDark};
      text-transform: uppercase;
      font-size: 0.9rem;
      line-height: 0.75rem;
      font-weight: 500;
      padding-top: ${({ theme }) => theme.space[1]};
    }
  }

  .product-price {
    padding-bottom: ${({ theme }) => theme.space[1]};
    font-size: 1.375rem;
    font-weight: bold;
  }

  ${({ theme }) => theme.below.md} {
    flex-direction: column;
    align-items: center;
    padding: 0;

    .product-image {
      width: 100%;
      max-width: 400px;
    }

    .product-description {
      width: 100%;
      max-width: 400px;
      padding-bottom: ${({ theme }) => theme.space[2]};
      text-align: center;

      &__action {
        margin-top: ${({ theme }) => theme.space[3]};
      }
    }

    .product-price {
      margin-top: 1rem;
    }

    .product-shortDescription {
      text-align: left;
    }

    .product-name {
      padding-top: ${({ theme }) => theme.space[2]};
      padding-bottom: 0;
    }
  }
`;

const RelatedProducts = productArticleNumber => {
  const articleNumber = productArticleNumber.productArticleNumber;

  return (
    <Query query={CrossSellQuery}>
      {({ data }) => {
        const product = get(data, 'product');

        return product ? (
          <BreakFlex>
            <SectionRow>
              <SectionHeading>{t('Matching product')}</SectionHeading>

              <SectionRowInner>
                <ProductSection>
                  <div className="product-image">
                    <Image
                      src={product.images[0].url}
                      cover={true}
                      aspect="3:4"
                      sizes={[1 / 5]}
                      alt={product.subName}
                    >
                      <AddFavoriteButton product={product} />
                    </Image>
                  </div>

                  <div className="product-description">
                    <div className="product-description__content">
                      <div className="product-name">
                        <h4>{product.name}</h4>
                        <span className="product-name__sub-title">
                          {product.subName}
                        </span>
                      </div>

                      <div className="product-price">
                        <Price price={product.price} />
                      </div>

                      <div className="product-shortDescription">
                        {product.shortDescription}
                      </div>
                    </div>

                    <div className="product-description__action">
                      <Button href={product.primaryRoute.path}>
                        {t('View product')}
                      </Button>
                    </div>
                  </div>
                </ProductSection>
              </SectionRowInner>
            </SectionRow>
          </BreakFlex>
        ) : null;
      }}
    </Query>
  );
};

export default RelatedProducts;
